import { Filter, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterToggle {
  key: string;
  label: string;
}

export interface RepOption {
  name: string;
  territories: readonly string[] | string[];
}

export interface FilterSection {
  id: string;
  label: string;
  type?: "checkbox" | "badge" | "toggle" | "rep" | "group" | "divider";
  options: FilterOption[];
  // For toggle type — single boolean
  toggleKey?: string;
  toggleLabel?: string;
  // For group type — multiple booleans in one section
  toggles?: FilterToggle[];
  // For rep type — rep→territory mapping
  reps?: readonly RepOption[] | RepOption[];
  // For divider type — section group header
  groupLabel?: string;
}

interface AccountFilterSidebarProps {
  totalCount: number;
  countLabel?: string;
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterSections: FilterSection[];
  filterValues: Record<string, string[]>;
  onFilterChange: (sectionId: string, values: string[]) => void;
  toggleValues?: Record<string, boolean>;
  onToggleChange?: (key: string, value: boolean) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  defaultOpenSections?: string[];
  variant?: "sidebar" | "card";
}

export function AccountFilterSidebar({
  totalCount,
  countLabel = "",
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  filterSections,
  filterValues,
  onFilterChange,
  toggleValues = {},
  onToggleChange,
  onClearFilters,
  hasActiveFilters,
  defaultOpenSections = [],
  variant = "sidebar",
}: AccountFilterSidebarProps) {
  const handleCheckboxChange = (sectionId: string, value: string, checked: boolean) => {
    const currentValues = filterValues[sectionId] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter((v) => v !== value);
    onFilterChange(sectionId, newValues);
  };

  const isChecked = (sectionId: string, value: string) => {
    return (filterValues[sectionId] || []).includes(value);
  };

  const getActiveCount = (sectionId: string) => {
    return (filterValues[sectionId] || []).length;
  };

  // Rep helpers
  const isRepSelected = (rep: RepOption) => {
    const selected = new Set(filterValues["territories"] || []);
    return rep.territories.every((t) => selected.has(t));
  };

  const toggleRep = (rep: RepOption) => {
    const current = filterValues["territories"] || [];
    if (isRepSelected(rep)) {
      const updated = current.filter((t) => !(rep.territories as readonly string[]).includes(t));
      onFilterChange("territories", updated);
    } else {
      const updated = [...new Set([...current, ...rep.territories])];
      onFilterChange("territories", updated);
    }
  };

  const countBadge = (count: number) =>
    count > 0 ? (
      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
        {count}
      </Badge>
    ) : null;

  const toggleBadge = (key: string) =>
    toggleValues[key] ? (
      <Badge variant="secondary" className="ml-auto text-xs h-5 px-1.5">
        On
      </Badge>
    ) : null;

  // ── Render a single section based on type ─────────────────────────

  const renderCheckboxSection = (section: FilterSection) => (
    <div className="space-y-2 pb-2 max-h-48 overflow-y-auto">
      {section.options.map((option) => (
        <div key={option.value} className="flex items-center gap-2">
          <Checkbox
            id={`${section.id}-${option.value}`}
            checked={isChecked(section.id, option.value)}
            onCheckedChange={(checked) =>
              handleCheckboxChange(section.id, option.value, checked as boolean)
            }
          />
          <label
            htmlFor={`${section.id}-${option.value}`}
            className="text-sm text-foreground cursor-pointer flex-1 flex items-center justify-between"
          >
            <span>{option.label}</span>
            {option.count !== undefined && (
              <span className="text-xs text-muted-foreground">
                {option.count.toLocaleString()}
              </span>
            )}
          </label>
        </div>
      ))}
      {section.options.length === 0 && (
        <p className="text-xs text-muted-foreground">No options available</p>
      )}
    </div>
  );

  const renderBadgeSection = (section: FilterSection) => (
    <div className="flex flex-wrap gap-2 pb-2 max-h-64 overflow-y-auto">
      {section.options.map((option) => (
        <Badge
          key={option.value}
          variant={isChecked(section.id, option.value) ? "default" : "outline"}
          className="cursor-pointer text-xs"
          onClick={() => {
            const current = filterValues[section.id] || [];
            const updated = current.includes(option.value)
              ? current.filter((v) => v !== option.value)
              : [...current, option.value];
            onFilterChange(section.id, updated);
          }}
        >
          {option.label}
          {option.count !== undefined && ` (${option.count})`}
        </Badge>
      ))}
      {section.options.length === 0 && (
        <p className="text-xs text-muted-foreground">No options available</p>
      )}
    </div>
  );

  const renderToggleSection = (section: FilterSection) => {
    const key = section.toggleKey || section.id;
    return (
      <div className="flex items-center space-x-2 pb-2">
        <Checkbox
          id={`toggle-${key}`}
          checked={toggleValues[key] || false}
          onCheckedChange={(checked) =>
            onToggleChange?.(key, checked === true)
          }
        />
        <label
          htmlFor={`toggle-${key}`}
          className="text-sm leading-none cursor-pointer font-medium"
        >
          {section.toggleLabel || section.label}
        </label>
      </div>
    );
  };

  const renderRepSection = (section: FilterSection) => {
    const reps = section.reps || [];
    return (
      <div className="space-y-2 pb-2">
        {reps.map((rep) => (
          <div key={rep.name} className="flex items-center space-x-2">
            <Checkbox
              id={`rep-${rep.name}`}
              checked={isRepSelected(rep)}
              onCheckedChange={() => toggleRep(rep)}
            />
            <label
              htmlFor={`rep-${rep.name}`}
              className="text-sm leading-none cursor-pointer"
            >
              {rep.name}
            </label>
          </div>
        ))}
        <div className="flex items-center space-x-2 pt-1 border-t">
          <Checkbox
            id="rep-unassigned"
            checked={(filterValues["territories"] || []).includes("__unassigned__")}
            onCheckedChange={(checked) => {
              const current = filterValues["territories"] || [];
              const updated = checked
                ? [...current, "__unassigned__"]
                : current.filter((v) => v !== "__unassigned__");
              onFilterChange("territories", updated);
            }}
          />
          <label
            htmlFor="rep-unassigned"
            className="text-sm leading-none text-muted-foreground italic cursor-pointer"
          >
            Unassigned
          </label>
        </div>
      </div>
    );
  };

  const renderGroupSection = (section: FilterSection) => {
    const toggleList = section.toggles || [];
    return (
      <div className="pb-2 space-y-3">
        {toggleList.map((toggle) => (
          <div key={toggle.key} className="flex items-center space-x-2">
            <Checkbox
              id={`group-${toggle.key}`}
              checked={toggleValues[toggle.key] || false}
              onCheckedChange={(checked) =>
                onToggleChange?.(toggle.key, checked === true)
              }
            />
            <label
              htmlFor={`group-${toggle.key}`}
              className="text-sm leading-none cursor-pointer"
            >
              {toggle.label}
            </label>
          </div>
        ))}
        {/* Checkbox sub-sections within the group */}
        {section.options.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            {section.options.map((option) => (
              <div key={option.value} className="flex items-center gap-2">
                <Checkbox
                  id={`${section.id}-${option.value}`}
                  checked={isChecked(section.id, option.value)}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange(section.id, option.value, checked as boolean)
                  }
                />
                <label
                  htmlFor={`${section.id}-${option.value}`}
                  className="text-sm text-foreground cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSectionContent = (section: FilterSection) => {
    const type = section.type || "checkbox";
    switch (type) {
      case "badge":
        return renderBadgeSection(section);
      case "toggle":
        return renderToggleSection(section);
      case "rep":
        return renderRepSection(section);
      case "group":
        return renderGroupSection(section);
      default:
        return renderCheckboxSection(section);
    }
  };

  const getSectionBadge = (section: FilterSection) => {
    const type = section.type || "checkbox";
    if (type === "toggle") return toggleBadge(section.toggleKey || section.id);
    if (type === "group") {
      const activeCount = (section.toggles || []).filter(
        (t) => toggleValues[t.key]
      ).length + getActiveCount(section.id);
      return countBadge(activeCount);
    }
    if (type === "rep") {
      const repCount = (filterValues["territories"] || []).length;
      return countBadge(repCount);
    }
    return countBadge(getActiveCount(section.id));
  };

  // ── Layout variants ───────────────────────────────────────────────

  const wrapperClass =
    variant === "card"
      ? "space-y-4 p-4 bg-card rounded-lg border"
      : "w-[280px] border-r bg-muted/30 flex flex-col h-full";

  if (variant === "card") {
    return (
      <div className={wrapperClass}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Filters</span>
            <Badge variant="secondary">
              {totalCount.toLocaleString()} {countLabel}
            </Badge>
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-8"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <Accordion
          type="multiple"
          defaultValue={defaultOpenSections}
          className="space-y-2"
        >
          {filterSections.map((section) =>
            section.type === "divider" ? (
              <div key={section.id} className="pt-3 pb-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {section.groupLabel || section.label}
                </p>
              </div>
            ) : (
            <AccordionItem
              key={section.id}
              value={section.id}
              className="border rounded-lg px-3"
            >
              <AccordionTrigger className="text-sm font-medium py-3">
                <div className="flex items-center gap-2">
                  {section.label} {getSectionBadge(section)}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {renderSectionContent(section)}
              </AccordionContent>
            </AccordionItem>
            )
          )}
        </Accordion>
      </div>
    );
  }

  // Default: sidebar variant
  return (
    <div className={wrapperClass}>
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <span className="flex items-center gap-2 font-medium text-foreground">
            <Filter className="h-4 w-4" />
            Filters
          </span>
          <Badge variant="outline" className="text-xs">
            {totalCount.toLocaleString()}
          </Badge>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-background"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          <Accordion
            type="multiple"
            defaultValue={defaultOpenSections}
            className="space-y-2"
          >
            {filterSections.map((section) => (
              <AccordionItem
                key={section.id}
                value={section.id}
                className="border rounded-lg px-3"
              >
                <AccordionTrigger className="py-3 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {section.label}
                    </span>
                    {getSectionBadge(section)}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {renderSectionContent(section)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </ScrollArea>

      {hasActiveFilters && (
        <div className="p-4 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onClearFilters}
          >
            <X className="h-4 w-4 mr-2" />
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
